// storeCatalog — the stores we can suggest at Shop Mode trip start.
//
// Derived (not hand-listed): walks every curated BRAND_EXPERTISE
// record's privateLabelFor array, so the catalog stays in lockstep
// with the house-brand data that actually powers the store boost in
// brandsForCanonical. A store only earns a pill if picking it can
// change a brand suggestion.
//
// Region filtering rides the brand record's own regions[] metadata:
// a Springfield user doesn't see stores whose only claiming brand is
// St-Louis-scoped. Recency (the user's own trip history) is layered
// on top at the call site — this module is static data only.

import { BRAND_EXPERTISE, normalizeStoreName } from "./brandExpertise";
import { brandMatchesRegion } from "../lib/useShoppingRegion";

// Build once at module load — the curated table is static.
function buildCatalog() {
  const byNorm = new Map(); // normalized name → { name, brandSlugs, regions }
  for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
    const stores = Array.isArray(rec?.privateLabelFor) ? rec.privateLabelFor : null;
    if (!stores) continue;
    for (const raw of stores) {
      const name = String(raw || "").trim();
      if (!name) continue;
      const key = normalizeStoreName(name);
      if (!key) continue;
      let entry = byNorm.get(key);
      if (!entry) {
        entry = { name, brandSlugs: [], regions: new Set(), scopes: new Set() };
        byNorm.set(key, entry);
      }
      entry.brandSlugs.push(slug);
      if (rec.scope) entry.scopes.add(rec.scope);
      for (const r of Array.isArray(rec.regions) ? rec.regions : []) {
        entry.regions.add(r);
      }
    }
  }
  return [...byNorm.values()].map((e) => ({
    name: e.name,
    brandSlugs: e.brandSlugs,
    regions: [...e.regions],
    // A store is "national" if any claiming brand is national/
    // international scope — used only for ordering below.
    national: e.scopes.has("national") || e.scopes.has("international"),
  }));
}

export const STORE_CATALOG = buildCatalog();

// Stores relevant to the user's region, national chains first (they
// exist everywhere), then regional grocers whose brand regions
// prefix-match. `limit` keeps the pill row scannable.
export function storeCatalogForRegion(region, { limit = 8 } = {}) {
  const out = [];
  for (const entry of STORE_CATALOG) {
    const matches = entry.national || entry.regions.length === 0 ||
      brandMatchesRegion({ regions: entry.regions }, region);
    if (matches) out.push(entry);
  }
  out.sort((a, b) => (b.national - a.national) || a.name.localeCompare(b.name));
  return out.slice(0, limit);
}
